import React, { useMemo, useContext, useEffect, useState } from "react";
import CountrySelect from '../CountrySelect';
import PaymentButton from '../PaymentButton';
import {EncryptedPubKey, PaymentInfoContext} from '../../App';
import PaymentSystemSelector from '../PaymentSystemSelector';
import axios from '../../api/axios';
import CardPayment from '../CardPayment';
import {CardPayment as EvyCardPayment} from '../EvyPay/CardPayment';
import AliPay from "../Apms/AliPay";
import AliPayCN from "../Apms/AlipayCN";
import GiroPay from "../Apms/GiroPay";
import WechatPay from "../Apms/WechatPay";
import Przelewy24 from "../Apms/Przelewy24";
import IDeal from "../Apms/IDeal";
import Sofort from "../Apms/Sofort";
import Tamara from "../Apms/Tamara";
import Bancontact from "../Apms/Bancontact";
import Multibanco from "../Apms/Multibanco";
import Eps from "../Apms/Eps";
import PostFinance from "../Apms/PostFinance";
import PayPal from "../Apms/PayPal";
import useStyles from './styles';
import { PaymentContext, PaymentMode } from "../../App";
import { PaymentModes } from "../../utils";
import {ProviderTypeContext} from "../../containers/InitCheckoutContainer";
import {ApmsTypes} from "../../constants";

const PaymentForm = ({
  formik, currencyList, errorString,
  currentPaymentSystem, setCurrentPaymentSystem,
  walletItem, setWalletItem, warningMessage,
}) => {
  const classes = useStyles();
  const payment = useContext(PaymentContext);
  const paymentMode = useContext(PaymentMode);
  const encryptedPubKey = useContext(EncryptedPubKey);
  const paymentInfo = useContext(PaymentInfoContext);
  const {providerType, setProviderType} = useContext(ProviderTypeContext);

  const [paymentSystems, setPaymentSystems] = useState();
  const [isPaymentByWalletAllowed, setIsPaymentByWalletAllowed] = useState({
    applePay: null,
    googlePay: null
  });

  const loadLpm = async () => {
    const { allowedLpms = [], applePay, googlePay} = await getPaymentSystems(formik.values.country);

    return {
        allowedLpms,
        applePay,
        googlePay,
    }
  }

  const isEvyPayAllowed = () => {
    // It's really not great solution. Need to discuss that with Oleksii I. when his back from vacation.
    return formik.values.country === 'RU';
  }

  useEffect(() => {
    if (formik.values.country && encryptedPubKey) {
      loadLpm().then(({ allowedLpms, applePay, googlePay }) => {
        const defaultPaymentSystems = [
            ...paymentInfo.allowedPaymentProviders.map((item) => ({
                code: String(item.provider).toLowerCase(),
                name: item.provider,
                key: item.publicKey
            }))
        ].filter((item) => {
          if (item.code === 'mpgs') {
            return false
          }
          if (item.code === 'paygate') {
            return false
          }
          // It's really not great solution. Need to discuss that with Oleksii I. when his back from vacation.
          if (item.code !== 'evypay') 
            return true
           else 
            return isEvyPayAllowed()
        })

        setPaymentSystems([
          ...defaultPaymentSystems,
          ...(allowedLpms || [])]
        );
        setIsPaymentByWalletAllowed({
          applePay,
          googlePay
        });

        if(!defaultPaymentSystems.map(({code}) => code).includes(currentPaymentSystem?.code)) {
          setCurrentPaymentSystem(defaultPaymentSystems[0]);
        }
      });
    }
  }, [formik.values.country, encryptedPubKey]);

  const getPaymentSystems = async (country) => {
    try {
      const { data } = await axios.get(`/lpm/${encryptedPubKey}/allowed?countryIso=${country}`);
      return data;
    } catch (error) {
      console.error(error);
    }
  };

  const paymentForm = useMemo(() => {
    switch (currentPaymentSystem?.code) {
      case 'checkout':
        return <CardPayment
            formik={formik}
            currencyList={currencyList}
            errorString={errorString}
            paymentMode={paymentMode}
            walletItem={walletItem}
            setWalletItem={setWalletItem}
            warningMessage={warningMessage}
        />
      case 'evypay':
        return <EvyCardPayment
            formik={formik}
            currencyList={currencyList}
            errorString={errorString}
            paymentMode={paymentMode}
            walletItem={walletItem}
            setWalletItem={setWalletItem}
            warningMessage={warningMessage}
        />
      case ApmsTypes.Alipay:
        return <AliPay currentPaymentSystem={currentPaymentSystem} errorString={errorString} />
      case ApmsTypes.AlipayCN:
        return <AliPayCN currentPaymentSystem={currentPaymentSystem} errorString={errorString} />
      case ApmsTypes.Giropay:
        return <GiroPay currentPaymentSystem={currentPaymentSystem} formik={formik} errorString={errorString} />
      case ApmsTypes.WeChatPay:
        return <WechatPay currentPaymentSystem={currentPaymentSystem} errorString={errorString} />
      case ApmsTypes.Przelewy24:
        return <Przelewy24 currentPaymentSystem={currentPaymentSystem} formik={formik} errorString={errorString} />
      case ApmsTypes.IDeal:
        return <IDeal currentPaymentSystem={currentPaymentSystem} formik={formik} errorString={errorString} paymentInfo={paymentInfo} />
      case ApmsTypes.Sofort:
        return <Sofort currentPaymentSystem={currentPaymentSystem} errorString={errorString} />
      case ApmsTypes.Tamara:
        return <Tamara currentPaymentSystem={currentPaymentSystem} formik={formik} errorString={errorString} />
      case ApmsTypes.Bancontact:
        return <Bancontact currentPaymentSystem={currentPaymentSystem} formik={formik} errorString={errorString} />
      case ApmsTypes.Multibanco:
        return <Multibanco currentPaymentSystem={currentPaymentSystem} formik={formik} errorString={errorString} />
      case ApmsTypes.EPS:
        return <Eps currentPaymentSystem={currentPaymentSystem} errorString={errorString} />
      case ApmsTypes.PostFinance:
        return <PostFinance currentPaymentSystem={currentPaymentSystem} formik={formik} errorString={errorString} />
      case ApmsTypes.PayPal:
        return <PayPal currentPaymentSystem={currentPaymentSystem} formik={formik} errorString={errorString} paymentInfo={paymentInfo} />
      default:
        return null;
    }
  }, [currentPaymentSystem, formik]);

  useEffect(() => {
    if (currentPaymentSystem && providerType !== currentPaymentSystem.code)
      setProviderType(currentPaymentSystem.code)
  }, [currentPaymentSystem]);

  return (
    <form className={`${classes.formWrapper} formElement`} onSubmit={formik.handleSubmit}>
      {!payment && paymentMode !== PaymentModes.Token && (
        <CountrySelect formik={formik} />
      )}
      <PaymentButton isPaymentByWalletAllowed={isPaymentByWalletAllowed} formik={formik} />
      {paymentMode !== PaymentModes.Token && ( <PaymentSystemSelector
        paymentSystems={paymentSystems}
        currentPaymentSystem={currentPaymentSystem}
        setCurrentPaymentSystem={setCurrentPaymentSystem}
      />)}
      {paymentForm}
      {/* {errorString && (
        <Typography align="center" color="danger" className={classes.error}>
          {errorString}
        </Typography>
      )} */}
    </form>
  );
};

export default PaymentForm;
